import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { useAppMessage } from "../components/AppMessage";
import { User, Phone, Lock, Loader2, Eye, EyeOff, Mail, Link2, Unlink, CheckCircle2, AlertCircle, Plus, Share2, Volume2, Mic, Settings, Bell, PhoneCall, Save, Download, Upload, Zap, XCircle, Calendar, Trash2, Pencil, Monitor, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { format } from "date-fns";
import EmailSignatureEditor from "../components/EmailSignatureEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Provider icons as SVG components
const GmailIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/>
  </svg>
);

const Office365Icon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M21.17 2.06A1.6 1.6 0 0 1 23 3.64v16.72a1.6 1.6 0 0 1-1.83 1.58l-7.67-1.14V3.2l7.67-1.14zM12.5 4.36v15.28L2.83 21.5A1.6 1.6 0 0 1 1 19.92V4.08a1.6 1.6 0 0 1 1.83-1.58l9.67 1.86z" fill="#0078D4"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 text-white" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Notification Sounds Component (rendered in Notifications tab)
const NotificationSoundsManager = () => {
  const [callSoundEnabled, setCallSoundEnabled] = useState(true);
  const [smsSoundEnabled, setSmsSoundEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`${API}/user/preferences/audio_settings`)
      .then(res => {
        const saved = res.data?.value;
        if (saved) {
          setCallSoundEnabled(saved.call_sound_enabled !== false);
          setSmsSoundEnabled(saved.sms_sound_enabled !== false);
        }
      })
      .catch(() => {}) // missing pref is fine — defaults apply
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Merge into existing audio_settings so mic/speaker fields are preserved
      const existing = await api.get(`${API}/user/preferences/audio_settings`);
      const current = existing.data?.value || {};
      await api.put(`${API}/user/preferences/audio_settings`, {
        value: { ...current, call_sound_enabled: callSoundEnabled, sms_sound_enabled: smsSoundEnabled }
      });
      toast.success('Notification sound settings saved');
    } catch {
      toast.error('Failed to save notification sound settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 className="h-5 w-5 text-indigo-600" />
          Notification Sounds
        </CardTitle>
        <CardDescription>
          Choose which events play a sound alert.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
            <div>
              <p className="text-sm font-medium text-slate-700">Incoming call ringtone</p>
              <p className="text-xs text-slate-500">Play a sound when an inbound call arrives</p>
            </div>
            <input
              type="checkbox"
              checked={callSoundEnabled}
              onChange={e => setCallSoundEnabled(e.target.checked)}
              className="h-4 w-4 rounded accent-indigo-600"
            />
          </label>
          <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
            <div>
              <p className="text-sm font-medium text-slate-700">SMS notification ding</p>
              <p className="text-xs text-slate-500">Play a sound when a new inbound SMS arrives</p>
            </div>
            <input
              type="checkbox"
              checked={smsSoundEnabled}
              onChange={e => setSmsSoundEnabled(e.target.checked)}
              className="h-4 w-4 rounded accent-indigo-600"
            />
          </label>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Audio Settings Manager Component
const AudioSettingsManager = () => {
  const [audioDevices, setAudioDevices] = useState({ microphones: [], speakers: [] });
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'granted', 'denied', 'prompt'
  const [testingMic, setTestingMic] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [rnnoiseEnabled, setRnnoiseEnabled] = useState(true);

  // Refs so the RAF loop and timeout can read/write current state without stale closures
  const testingMicRef    = useRef(false);
  const audioContextRef  = useRef(null);
  const micStreamRef     = useRef(null);

  // Centralised teardown — safe to call multiple times (guards against double-close)
  const stopMicTest = () => {
    testingMicRef.current = false;
    setTestingMic(false);
    setMicLevel(0);
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    audioContextRef.current = null;
  };

  useEffect(() => {
    loadAudioSettings();
    return () => stopMicTest(); // clean up on tab-switch / unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAudioSettings = async () => {
    try {
      const prefsRes = await api.get(`${API}/user/preferences/audio_settings`).catch(() => null);
      const saved = prefsRes?.data?.value;

      if (saved) {
        setSelectedMic(saved.microphone_id || '');
        setSelectedSpeaker(saved.speaker_id || '');
        setRnnoiseEnabled(saved.rnnoise_enabled !== false); // default true
      }

      // Then enumerate devices
      await enumerateDevices();
    } catch (error) {
      console.error('Failed to load audio settings:', error);
      await enumerateDevices();
    } finally {
      setLoading(false);
    }
  };

  const enumerateDevices = async () => {
    try {
      // Check if mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        toast.error('Your browser does not support audio device selection');
        return;
      }

      // Request permission first to get device labels
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        setPermissionStatus('granted');
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          setPermissionStatus('denied');
          return;
        }
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const microphones = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          id: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}...`
        }));
      
      const speakers = devices
        .filter(device => device.kind === 'audiooutput')
        .map(device => ({
          id: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.slice(0, 8)}...`
        }));

      setAudioDevices({ microphones, speakers });

      // Set defaults if nothing selected
      if (!selectedMic && microphones.length > 0) {
        setSelectedMic(microphones[0].id);
      }
      if (!selectedSpeaker && speakers.length > 0) {
        setSelectedSpeaker(speakers[0].id);
      }
    } catch (error) {
      console.error('Failed to enumerate devices:', error);
      toast.error('Failed to detect audio devices');
    }
  };

  const requestPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setPermissionStatus('granted');
      await enumerateDevices();
      toast.success('Microphone access granted');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setPermissionStatus('denied');
        toast.error('Microphone access denied. Please allow access in your browser settings.');
      }
    }
  };

  const saveAudioSettings = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/user/preferences/audio_settings`, {
        value: {
          microphone_id: selectedMic,
          speaker_id: selectedSpeaker,
          rnnoise_enabled: rnnoiseEnabled,
          updated_at: new Date().toISOString()
        }
      });
      toast.success('Audio settings saved');
    } catch (error) {
      toast.error('Failed to save audio settings');
    } finally {
      setSaving(false);
    }
  };

  const testMicrophone = async () => {
    // If already testing, stop
    if (testingMicRef.current) {
      stopMicTest();
      return;
    }

    testingMicRef.current = true;
    setTestingMic(true);
    try {
      const constraints = selectedMic
        ? { audio: { deviceId: { exact: selectedMic } } }
        : { audio: true };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      micStreamRef.current = stream;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      analyser.smoothingTimeConstant = 0.8;
      analyser.fftSize = 1024;
      microphone.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const checkLevel = () => {
        // Read from ref — never stale, unlike the closure-captured state variable
        if (!testingMicRef.current) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setMicLevel(Math.min(100, average * 2));
        requestAnimationFrame(checkLevel);
      };

      checkLevel();

      // Auto-stop after 5 seconds — stopMicTest is idempotent so safe even if
      // the user already clicked Stop before the timer fires
      setTimeout(stopMicTest, 5000);
    } catch (error) {
      toast.error('Failed to test microphone');
      stopMicTest();
    }
  };

  const testSpeaker = async () => {
    try {
      const audio = new Audio('/test-tone.mp3');
      
      // Try to set output device if supported
      if (audio.setSinkId && selectedSpeaker) {
        try {
          await audio.setSinkId(selectedSpeaker);
        } catch (err) {
          // setSinkId not fully supported — ignore
        }
      }
      
      // Use a simple beep if no test file
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 440;
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 500);
      
      toast.success('Playing test tone');
    } catch (error) {
      toast.error('Failed to test speaker');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (permissionStatus === 'denied') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5 text-indigo-600" />
            Audio Settings
          </CardTitle>
          <CardDescription>
            Configure your microphone and speaker for phone calls.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Microphone Access Required</h3>
            <p className="text-slate-600 mb-4 max-w-md">
              To configure audio settings, please allow microphone access in your browser settings 
              and refresh this page.
            </p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (permissionStatus === 'prompt') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5 text-indigo-600" />
            Audio Settings
          </CardTitle>
          <CardDescription>
            Configure your microphone and speaker for phone calls.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <Mic className="h-8 w-8 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Allow Microphone Access</h3>
            <p className="text-slate-600 mb-4 max-w-md">
              We need access to your microphone to detect available audio devices 
              and configure your call settings.
            </p>
            <Button onClick={requestPermission} data-testid="allow-mic-btn">
              <Mic className="h-4 w-4 mr-2" />
              Allow Microphone Access
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 className="h-5 w-5 text-indigo-600" />
          Audio Settings
        </CardTitle>
        <CardDescription>
          Configure your microphone and speaker for phone calls. These settings will be remembered for future calls.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Microphone Selection */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-slate-500" />
            Microphone
          </Label>
          <div className="flex gap-2">
            <Select value={selectedMic} onValueChange={setSelectedMic}>
              <SelectTrigger className="flex-1" data-testid="mic-select">
                <SelectValue placeholder="Select microphone" />
              </SelectTrigger>
              <SelectContent>
                {audioDevices.microphones.map((device) => (
                  <SelectItem key={device.id} value={device.id}>
                    {device.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={testMicrophone}
              className={testingMic ? 'bg-green-50 border-green-300' : ''}
              data-testid="test-mic-btn"
            >
              {testingMic ? 'Testing...' : 'Test'}
            </Button>
          </div>
          
          {/* Mic Level Indicator */}
          {testingMic && (
            <div className="space-y-1">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all duration-100"
                  style={{ width: `${micLevel}%` }}
                />
              </div>
              <p className="text-xs text-slate-500">Speak to test your microphone</p>
            </div>
          )}
          
          {audioDevices.microphones.length === 0 && (
            <p className="text-sm text-amber-600">No microphones detected</p>
          )}
        </div>

        <Separator />

        {/* Speaker Selection */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-slate-500" />
            Speaker
          </Label>
          <div className="flex gap-2">
            <Select value={selectedSpeaker} onValueChange={setSelectedSpeaker}>
              <SelectTrigger className="flex-1" data-testid="speaker-select">
                <SelectValue placeholder="Select speaker" />
              </SelectTrigger>
              <SelectContent>
                {audioDevices.speakers.map((device) => (
                  <SelectItem key={device.id} value={device.id}>
                    {device.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={testSpeaker}
              data-testid="test-speaker-btn"
            >
              Test
            </Button>
          </div>
          {audioDevices.speakers.length === 0 && (
            <p className="text-sm text-amber-600">No speakers detected (using system default)</p>
          )}
        </div>

        <Separator />

        {/* AI Noise Suppression */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">AI Noise Suppression</Label>
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4">
            <div>
              <p className="text-sm font-medium text-slate-700">RNNoise Filter</p>
              <p className="text-xs text-slate-500">Removes background noise before your voice reaches the other party. Works on all browsers.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={rnnoiseEnabled}
                onChange={(e) => setRnnoiseEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600" />
            </label>
          </div>
        </div>

        <Separator />

        {/* Info */}
        <div className="bg-slate-50 rounded-lg p-4">
          <p className="text-sm text-slate-600">
            <strong>Tip:</strong> These settings will be used when making calls through the CRM.
            Make sure your selected devices are connected and working properly.
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={saveAudioSettings} 
            disabled={saving}
            data-testid="save-audio-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Settings className="mr-2 h-4 w-4" />
                Save Audio Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Task Notification Rules Manager ───────────────────────────────────────
const PRESET_OPTIONS = [
  { key: '5m',   label: '5 min before',         value: 5,    unit: 'minutes' },
  { key: '15m',  label: '15 min before',         value: 15,   unit: 'minutes' },
  { key: '30m',  label: '30 min before',         value: 30,   unit: 'minutes' },
  { key: '1h',   label: '1 hour before',         value: 60,   unit: 'minutes' },
  { key: '2h',   label: '2 hours before',        value: 120,  unit: 'minutes' },
  { key: '1d',   label: '1 day before',          value: 1,    unit: 'days' },
  { key: '1bd',  label: '1 business day before', value: 1,    unit: 'business_days' },
  { key: 'custom', label: 'Custom…',             value: null, unit: null },
];

const NOTIFY_TYPES = [
  { key: 'email', label: 'Email',     icon: <Mail className="h-4 w-4" />,     color: 'border-blue-400 bg-blue-50 text-blue-700' },
  { key: 'sms',   label: 'SMS',      icon: <Phone className="h-4 w-4" />,    color: 'border-green-400 bg-green-50 text-green-700' },
  { key: 'call',  label: 'Phone Call', icon: <PhoneCall className="h-4 w-4" />, color: 'border-orange-400 bg-orange-50 text-orange-700' },
  { key: 'popup', label: 'Popup',    icon: <Monitor className="h-4 w-4" />,  color: 'border-purple-400 bg-purple-50 text-purple-700' },
];

const CUSTOM_UNITS = [
  { value: 'minutes',       label: 'minutes before' },
  { value: 'hours',         label: 'hours before' },
  { value: 'days',          label: 'days before' },
  { value: 'business_days', label: 'business days before' },
];

const genId = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
  const r = (Math.random() * 16) | 0;
  return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
});

const emptyForm = { label: '', type: 'email', preset: '30m', customValue: 1, customUnit: 'hours', specificDate: '', specificTime: '', autoDelete: false, repeatDaily: false };

const formatRuleLabel = (rule) => {
  if (rule.timing_type === 'specific') {
    return `At ${rule.specific_date} ${rule.specific_time}`;
  }
  const presetMap = { '5m':'5 min','15m':'15 min','30m':'30 min','1h':'1 hour','2h':'2 hours','1d':'1 day','1bd':'1 business day' };
  if (rule.preset && rule.preset !== 'custom' && presetMap[rule.preset]) return `${presetMap[rule.preset]} before`;
  const unitLabel = { minutes:'min', hours:'hr', days:'day', business_days:'biz day' }[rule.unit] || rule.unit;
  return `${rule.value} ${unitLabel}${rule.value !== 1 && !['minutes','hours'].includes(rule.unit) ? 's' : ''} before`;
};

const TaskNotificationManager = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { loadRules(); }, []);

  const loadRules = async () => {
    try {
      const res = await api.get(`${API}/user/preferences/task_notification_rules`);
      setRules(Array.isArray(res.data?.value) ? res.data.value : []);
    } catch { setRules([]); }
    finally { setLoading(false); }
  };

  const persistRules = async (newRules) => {
    await api.put(`${API}/user/preferences/task_notification_rules`, { value: newRules });
    setRules(newRules);
  };

  const openAdd = () => { setEditingRule(null); setForm(emptyForm); setDialogOpen(true); };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      label: rule.label || '',
      type: rule.type,
      preset: rule.preset || '30m',
      customValue: rule.value || 1,
      customUnit: rule.unit || 'hours',
      specificDate: rule.specific_date || '',
      specificTime: rule.specific_time || '',
      autoDelete: rule.auto_delete || false,
      repeatDaily: rule.repeat_daily || false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const isSpecific = form.preset === 'specific';
    const isCustom   = form.preset === 'custom';

    if (isSpecific && (!form.specificDate || !form.specificTime)) {
      toast.error('Please pick a date and time'); return;
    }
    if (isCustom && (!form.customValue || form.customValue < 1)) {
      toast.error('Enter a valid number'); return;
    }

    setSaving(true);
    try {
      let rule;
      if (isSpecific) {
        rule = { type: form.type, preset: 'specific', timing_type: 'specific',
                 specific_date: form.specificDate, specific_time: form.specificTime };
      } else if (isCustom) {
        rule = { type: form.type, preset: 'custom', timing_type: 'before',
                 value: Number(form.customValue), unit: form.customUnit };
      } else {
        const p = PRESET_OPTIONS.find(o => o.key === form.preset);
        rule = { type: form.type, preset: form.preset, timing_type: 'before',
                 value: p.value, unit: p.unit };
      }
      rule.label = form.label.trim();
      rule.auto_delete = form.autoDelete;
      rule.repeat_daily = form.repeatDaily;

      let newRules;
      if (editingRule) {
        newRules = rules.map(r => r.id === editingRule.id ? { ...rule, id: r.id } : r);
      } else {
        newRules = [...rules, { ...rule, id: genId() }];
      }
      await persistRules(newRules);
      toast.success(editingRule ? 'Rule updated' : 'Rule added');
      setDialogOpen(false);
    } catch { toast.error('Failed to save rule'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await persistRules(rules.filter(r => r.id !== deleteId));
      toast.success('Rule deleted');
      setDeleteId(null);
    } catch { toast.error('Failed to delete rule'); }
  };

  const typeConf = Object.fromEntries(NOTIFY_TYPES.map(t => [t.key, t]));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-indigo-600" /> Task Notification Rules
            </CardTitle>
            <CardDescription>
              Set how and when to be notified about scheduled calls &amp; meetings. Rules apply to all future tasks.
            </CardDescription>
          </div>
          <Button size="sm" onClick={openAdd}><Plus className="h-4 w-4 mr-1" /> Add Rule</Button>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : rules.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed rounded-lg text-slate-400">
            <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No notification rules yet.</p>
            <p className="text-xs mt-1">Add rules to receive reminders for calls &amp; meetings.</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={openAdd}><Plus className="h-4 w-4 mr-1" /> Add First Rule</Button>
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map(rule => {
              const tc = typeConf[rule.type] || typeConf.email;
              return (
                <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg bg-slate-50 hover:bg-white transition-colors">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${tc.color}`}>
                      {tc.icon} {tc.label}
                    </span>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {rule.label && (
                          <p className="text-sm font-semibold text-slate-800">{rule.label}</p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-slate-700">
                            {rule.repeat_daily ? 'Daily digest' : formatRuleLabel(rule)}
                          </p>
                          {rule.repeat_daily && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">Daily</span>
                          )}
                          {rule.auto_delete && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">One-time</span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">
                        {rule.type === 'email' ? 'Sent to your registered email' :
                         rule.type === 'sms'   ? 'SMS to your phone number' :
                         rule.type === 'call'  ? 'Outbound call to your phone' :
                                                 'Browser popup notification'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(rule)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteId(rule.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {rules.length > 0 && (
          <p className="mt-4 text-xs text-slate-500 bg-indigo-50 border border-indigo-100 rounded p-3">
            <strong>Note:</strong> SMS &amp; Phone Call require a phone number in your profile. Popup notifications require browser permission granted. Email CC can be configured below.
          </p>
        )}
      </CardContent>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Notification Rule' : 'Add Notification Rule'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Label / description */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">What is this notification for? <span className="text-slate-400 font-normal">(optional)</span></Label>
              <input
                type="text"
                placeholder="e.g. Daily standup reminder, Follow-up call alert…"
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                maxLength={80}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 placeholder:text-slate-400"
              />
            </div>

            {/* Notification Type */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Notification Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {NOTIFY_TYPES.map(t => (
                  <button key={t.key} type="button"
                    onClick={() => setForm(f => ({ ...f, type: t.key }))}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 text-sm font-medium transition-all
                      ${form.type === t.key ? `${t.color} border-current` : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
              {form.type === 'sms' && (
                <p className="text-xs text-amber-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Requires a phone number saved in your profile.</p>
              )}
              {form.type === 'call' && (
                <p className="text-xs text-orange-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> System will call your phone number with a spoken reminder.</p>
              )}
              {form.type === 'popup' && (
                <p className="text-xs text-purple-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Browser must be open and notification permission granted.</p>
              )}
            </div>

            {/* When to notify — hidden for daily-digest rules */}
            {!form.repeatDaily && <div className="space-y-2">
              <Label className="text-sm font-semibold">When to Notify</Label>

              {/* Preset grid */}
              <div className="grid grid-cols-4 gap-1.5">
                {PRESET_OPTIONS.filter(p => p.key !== 'custom').map(p => (
                  <button key={p.key} type="button"
                    onClick={() => setForm(f => ({ ...f, preset: p.key }))}
                    className={`px-2 py-2 rounded-md border text-xs font-medium transition-all
                      ${form.preset === p.key ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  >
                    {p.label}
                  </button>
                ))}
                {/* Custom button */}
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, preset: 'custom' }))}
                  className={`px-2 py-2 rounded-md border text-xs font-medium transition-all col-span-2
                    ${form.preset === 'custom' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                >
                  Custom interval…
                </button>
                {/* Specific date/time button */}
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, preset: 'specific' }))}
                  className={`px-2 py-2 rounded-md border text-xs font-medium transition-all col-span-2
                    ${form.preset === 'specific' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                >
                  <Calendar className="h-3 w-3 inline mr-1" />Specific date &amp; time
                </button>
              </div>

              {/* Custom interval inputs */}
              {form.preset === 'custom' && (
                <div className="flex items-center gap-2 mt-2">
                  <input type="number" min="1" max="999"
                    value={form.customValue}
                    onChange={e => setForm(f => ({ ...f, customValue: e.target.value }))}
                    className="w-20 border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                  <Select value={form.customUnit} onValueChange={v => setForm(f => ({ ...f, customUnit: v }))}>
                    <SelectTrigger className="flex-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CUSTOM_UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Specific date & time inputs */}
              {form.preset === 'specific' && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-slate-500">Date</Label>
                    <input type="date"
                      value={form.specificDate}
                      onChange={e => setForm(f => ({ ...f, specificDate: e.target.value }))}
                      className="w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-slate-500">Time</Label>
                    <input type="time"
                      value={form.specificTime}
                      onChange={e => setForm(f => ({ ...f, specificTime: e.target.value }))}
                      className="w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                </div>
              )}
            </div>}

            {/* Options */}
            <div className="space-y-3 pt-1 border-t border-slate-100">
              <Label className="text-sm font-semibold">Options</Label>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium text-slate-700">Repeat daily</p>
                  <p className="text-xs text-slate-500">
                    {form.repeatDaily
                      ? 'Sends a daily digest listing all your upcoming calls & meetings'
                      : 'Fire once per day — sends a summary of all pending tasks'}
                  </p>
                </div>
                <Switch checked={form.repeatDaily} onCheckedChange={v => setForm(f => ({ ...f, repeatDaily: v }))} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium text-slate-700">Auto-delete after firing</p>
                  <p className="text-xs text-slate-500">Remove this rule automatically after it triggers once</p>
                </div>
                <Switch checked={form.autoDelete} onCheckedChange={v => setForm(f => ({ ...f, autoDelete: v }))} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRule ? 'Save Changes' : 'Add Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this notification rule?</AlertDialogTitle>
            <AlertDialogDescription>Future scheduled tasks will no longer trigger this notification.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

// ─── Email CC Configuration ─────────────────────────────────────────────────
const EmailCCManager = () => {
  const [ccEmails, setCcEmails] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`${API}/user/preferences/notification_email_cc`)
      .then(r => setCcEmails(Array.isArray(r.data?.value) ? r.data.value : []))
      .catch(() => setCcEmails([]))
      .finally(() => setLoading(false));
  }, []);

  const persist = async (list) => {
    setSaving(true);
    try {
      await api.put(`${API}/user/preferences/notification_email_cc`, { value: list });
      setCcEmails(list);
      toast.success('CC list saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addEmail = () => {
    const email = inputVal.trim().toLowerCase();
    if (!email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (ccEmails.includes(email)) { toast.error('Already added'); return; }
    persist([...ccEmails, email]);
    setInputVal('');
  };

  const removeEmail = (em) => persist(ccEmails.filter(e => e !== em));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-4 w-4 text-blue-600" /> Email Notification CC
        </CardTitle>
        <CardDescription>
          These addresses will be CC'd on every task email notification you receive.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={inputVal}
                onChange={e => setInputVal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addEmail()}
                className="flex-1"
              />
              <Button size="sm" onClick={addEmail} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
            {ccEmails.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {ccEmails.map(em => (
                  <Badge key={em} variant="secondary" className="flex items-center gap-1.5 pr-1">
                    {em}
                    <button onClick={() => removeEmail(em)} className="ml-1 hover:text-red-600 transition-colors">
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400">No CC recipients added yet.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Notification Settings Component
const NotificationSettingsManager = () => {
  const [receiveLogReminders, setReceiveLogReminders] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadNotificationSettings();
  }, []);

  const loadNotificationSettings = async () => {
    try {
      const res = await api.get(`${API}/user/preferences/receive_log_reminders`);
      // Default to true if not set
      if (res.data?.value !== undefined && res.data?.value !== null) {
        setReceiveLogReminders(res.data.value);
      } else {
        setReceiveLogReminders(true);
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
      setReceiveLogReminders(true);
    } finally {
      setLoading(false);
    }
  };

  const saveNotificationSettings = async (newValue) => {
    setSaving(true);
    try {
      await api.put(`${API}/user/preferences/receive_log_reminders`, {
        value: newValue
      });
      setReceiveLogReminders(newValue);
      toast.success(newValue ? 'Log reminders enabled' : 'Log reminders disabled');
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      toast.error('Failed to save notification settings');
      // Revert on error
      setReceiveLogReminders(!newValue);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-amber-600" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Control which email notifications you receive from DailyLog.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Log Reminders Toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-0.5">
            <Label className="text-base font-medium">Receive Log Reminders</Label>
            <p className="text-sm text-slate-500">
              Get email reminders when you haven&apos;t submitted your daily log.
              Reminders are sent at scheduled times on weekdays.
            </p>
          </div>
          <Switch
            checked={receiveLogReminders}
            onCheckedChange={saveNotificationSettings}
            disabled={saving}
            data-testid="toggle-log-reminders"
          />
        </div>

        {/* Info Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> When disabled, you will not receive any automatic reminder emails 
            about missing daily logs. You can still submit logs manually at any time.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Telnyx Settings Manager Component
const TelnyxSettingsManager = () => {
  const msg = useAppMessage();
  const [settings, setSettings] = useState({
    api_key: '',
    phone_number: '',
    connection_id: '',
    messaging_profile_id: '',
    webrtc_username: '',
    webrtc_password: '',
  });
  const [existingSettings, setExistingSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [settingUpInbound, setSettingUpInbound] = useState(false);
  const [inboundResults, setInboundResults] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showWebrtcPassword, setShowWebrtcPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get(`${API}/telnyx/settings`);
      setExistingSettings(res.data);
      // Pre-fill non-sensitive fields
      setSettings(prev => ({
        ...prev,
        phone_number: res.data.phone_number || '',
        connection_id: res.data.connection_id || '',
        messaging_profile_id: res.data.messaging_profile_id || '',
        webrtc_username: res.data.webrtc_username || '',
      }));
    } catch (error) {
      console.error('Failed to load Telnyx settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send fields that have values
      const updateData = {};
      if (settings.api_key) updateData.api_key = settings.api_key;
      if (settings.phone_number) updateData.phone_number = settings.phone_number;
      if (settings.connection_id) updateData.connection_id = settings.connection_id;
      if (settings.messaging_profile_id) updateData.messaging_profile_id = settings.messaging_profile_id;
      if (settings.webrtc_username) updateData.webrtc_username = settings.webrtc_username;
      if (settings.webrtc_password) updateData.webrtc_password = settings.webrtc_password;

      await api.put(`${API}/telnyx/settings`, updateData);
      toast.success('Telnyx settings saved successfully');
      
      // Clear sensitive fields and reload
      setSettings(prev => ({ ...prev, api_key: '', webrtc_password: '' }));
      loadSettings();
    } catch (error) {
      console.error('Failed to save Telnyx settings:', error);
      toast.error('Failed to save Telnyx settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!(await msg.confirm('Delete Telnyx settings?', 'Are you sure you want to delete all your Telnyx settings?', { confirmLabel: 'Delete', destructive: true }))) return;
    
    try {
      await api.delete(`${API}/telnyx/settings`);
      toast.success('Telnyx settings deleted');
      setSettings({
        api_key: '',
        phone_number: '',
        connection_id: '',
        messaging_profile_id: '',
        webrtc_username: '',
        webrtc_password: '',
      });
      setExistingSettings(null);
      setTestResults(null);
    } catch (error) {
      console.error('Failed to delete Telnyx settings:', error);
      toast.error('Failed to delete Telnyx settings');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResults(null);
    try {
      const res = await api.post(`${API}/telnyx/test-connection`);
      setTestResults(res.data);
      
      if (res.data.success) {
        toast.success(res.data.summary);
      } else {
        toast.error(res.data.summary);
      }
    } catch (error) {
      console.error('Test connection error:', error);
      setTestResults({
        success: false,
        summary: 'Test failed',
        checks: [{
          name: 'Connection Test',
          status: 'error',
          message: error.response?.data?.detail || 'Failed to test connection'
        }]
      });
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSetupInbound = async () => {
    setSettingUpInbound(true);
    setInboundResults(null);
    try {
      const res = await api.post(`${API}/telnyx/setup-inbound`);
      setInboundResults(res.data);
      if (res.data.success) toast.success(res.data.summary);
      else toast.error(res.data.summary);
    } catch (error) {
      console.error('Setup inbound error:', error);
      const detail = error.response?.data?.detail || 'Failed to configure inbound calling';
      setInboundResults({ success: false, summary: detail, steps: [] });
      toast.error(detail);
    } finally {
      setSettingUpInbound(false);
    }
  };

  const handleExport = () => {
    // Create export data with current settings
    const exportData = {
      phone_number: settings.phone_number || '',
      connection_id: settings.connection_id || '',
      messaging_profile_id: settings.messaging_profile_id || '',
      webrtc_username: settings.webrtc_username || '',
      // Note: Sensitive fields (api_key, webrtc_password) are NOT exported for security
      exported_at: new Date().toISOString(),
      note: 'API Key and WebRTC Password are not included for security. You must re-enter them after import.'
    };

    // Create JSON file and download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `telnyx-settings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Settings exported successfully');
  };

  const handleImport = () => {
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      try {
        const text = await file.text();
        const importedData = JSON.parse(text);
        
        // Validate imported data
        if (typeof importedData !== 'object') {
          throw new Error('Invalid JSON format');
        }
        
        // Update settings with imported data (excluding sensitive fields)
        setSettings(prev => ({
          ...prev,
          phone_number: importedData.phone_number || prev.phone_number,
          connection_id: importedData.connection_id || prev.connection_id,
          messaging_profile_id: importedData.messaging_profile_id || prev.messaging_profile_id,
          webrtc_username: importedData.webrtc_username || prev.webrtc_username,
          // Keep existing sensitive fields unchanged
        }));
        
        toast.success('Settings imported successfully. Remember to enter your API Key and WebRTC Password.');
      } catch (error) {
        console.error('Import error:', error);
        toast.error('Failed to import settings. Please check the file format.');
      }
    };
    
    input.click();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PhoneCall className="h-5 w-5 text-green-600" />
          Telnyx Phone Settings
        </CardTitle>
        <CardDescription>
          Configure your Telnyx credentials to make calls and send SMS from the CRM.
          Get your credentials from the{' '}
          <a 
            href="https://portal.telnyx.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Telnyx Portal
          </a>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Indicator */}
        {existingSettings?.api_key_set && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-800">
              Telnyx is configured. Your API key ends in: {existingSettings.api_key?.slice(-4)}
            </span>
          </div>
        )}

        {/* API Key */}
        <div className="space-y-2">
          <Label htmlFor="telnyx-api-key">API Key (V2) *</Label>
          <div className="relative">
            <Input
              id="telnyx-api-key"
              type={showApiKey ? "text" : "password"}
              placeholder={existingSettings?.api_key_set ? "••••••••••••••••" : "KEY..."}
              value={settings.api_key}
              onChange={(e) => setSettings(prev => ({ ...prev, api_key: e.target.value }))}
              data-testid="telnyx-api-key"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3"
              onClick={() => setShowApiKey(!showApiKey)}
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-slate-500">Found in Telnyx Portal → API Keys</p>
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <Label htmlFor="telnyx-phone">Phone Number *</Label>
          <Input
            id="telnyx-phone"
            type="tel"
            placeholder="732-394-1313"
            value={settings.phone_number}
            onChange={(e) => setSettings(prev => ({ ...prev, phone_number: e.target.value }))}
            data-testid="telnyx-phone"
          />
          <p className="text-xs text-slate-500">Your Telnyx phone number (will be formatted automatically)</p>
        </div>

        {/* Connection ID */}
        <div className="space-y-2">
          <Label htmlFor="telnyx-connection">Connection ID *</Label>
          <Input
            id="telnyx-connection"
            placeholder="e.g., 1234567890123456789"
            value={settings.connection_id}
            onChange={(e) => setSettings(prev => ({ ...prev, connection_id: e.target.value }))}
            data-testid="telnyx-connection"
          />
          <p className="text-xs text-slate-500">Found in Telnyx Portal → Voice → SIP Connections or TeXML Apps</p>
        </div>

        {/* Messaging Profile ID */}
        <div className="space-y-2">
          <Label htmlFor="telnyx-messaging">Messaging Profile ID (for SMS)</Label>
          <Input
            id="telnyx-messaging"
            placeholder="e.g., 40017d21-1234-5678-abcd-1234567890ab"
            value={settings.messaging_profile_id}
            onChange={(e) => setSettings(prev => ({ ...prev, messaging_profile_id: e.target.value }))}
            data-testid="telnyx-messaging"
          />
          <p className="text-xs text-slate-500">Found in Telnyx Portal → Messaging → Messaging Profiles</p>
        </div>

        <hr className="border-slate-200" />

        <div className="space-y-1">
          <Label className="text-base font-medium">WebRTC Settings (for in-browser calls)</Label>
          <p className="text-sm text-slate-500">Required for making calls directly in your browser</p>
        </div>

        {/* WebRTC Username */}
        <div className="space-y-2">
          <Label htmlFor="telnyx-webrtc-user">WebRTC SIP Username</Label>
          <Input
            id="telnyx-webrtc-user"
            placeholder="your-sip-username"
            value={settings.webrtc_username}
            onChange={(e) => setSettings(prev => ({ ...prev, webrtc_username: e.target.value }))}
            data-testid="telnyx-webrtc-user"
          />
          <p className="text-xs text-slate-500">Found in your Credential Connection settings</p>
        </div>

        {/* WebRTC Password */}
        <div className="space-y-2">
          <Label htmlFor="telnyx-webrtc-pass">WebRTC SIP Password</Label>
          <div className="relative">
            <Input
              id="telnyx-webrtc-pass"
              type={showWebrtcPassword ? "text" : "password"}
              placeholder={existingSettings?.webrtc_password_set ? "••••••••••" : "Enter password"}
              value={settings.webrtc_password}
              onChange={(e) => setSettings(prev => ({ ...prev, webrtc_password: e.target.value }))}
              data-testid="telnyx-webrtc-pass"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3"
              onClick={() => setShowWebrtcPassword(!showWebrtcPassword)}
            >
              {showWebrtcPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Getting started:</strong> Create a{' '}
            <a href="https://portal.telnyx.com/#/app/connections/credential" target="_blank" rel="noopener noreferrer" className="underline">
              Credential Connection
            </a>{' '}
            in your Telnyx Portal for WebRTC calls. The Connection ID, Username, and Password come from there.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4">
          {/* Test Connection Button */}
          <Button 
            variant="outline" 
            onClick={handleTest}
            disabled={testing || !existingSettings?.api_key_set}
            className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            {testing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing Connection...
              </>
            ) : (
              <>
                <Zap className="mr-2 h-4 w-4" />
                Test Telnyx Connection
              </>
            )}
          </Button>

          {/* Setup Inbound Calling */}
          <Button
            variant="outline"
            onClick={handleSetupInbound}
            disabled={settingUpInbound || !existingSettings?.api_key_set || !existingSettings?.phone_number || !existingSettings?.connection_id}
            className="w-full border-green-300 text-green-700 hover:bg-green-50"
          >
            {settingUpInbound ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Configuring Inbound Routing...
              </>
            ) : (
              <>
                <PhoneCall className="mr-2 h-4 w-4" />
                Configure Inbound Calling
              </>
            )}
          </Button>

          {inboundResults && (
            <Card className={inboundResults.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}>
              <CardContent className="pt-6 space-y-3">
                <div className="flex items-start gap-3">
                  {inboundResults.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-semibold ${inboundResults.success ? 'text-green-900' : 'text-red-900'}`}>
                      {inboundResults.summary}
                    </h4>
                    {inboundResults.steps && inboundResults.steps.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {inboundResults.steps.map((s, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            {s.ok ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600 mt-0.5" />
                            )}
                            <div>
                              <span className="font-medium">{s.name}:</span>{' '}
                              <span className={s.ok ? 'text-green-800' : 'text-red-800'}>{s.message}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {inboundResults.webhook_url && (
                      <div className="mt-3 text-xs text-slate-600">
                        <span className="font-semibold">Webhook URL:</span>{' '}
                        <span className="font-mono break-all">{inboundResults.webhook_url}</span>
                      </div>
                    )}
                    {inboundResults.success && (
                      <p className="mt-3 text-xs text-green-800">
                        Keep a JTCRM tab open in your browser — inbound calls to your Telnyx number will ring there.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Test Results */}
          {testResults && (
            <Card className={testResults.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start gap-3">
                  {testResults.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-semibold ${testResults.success ? 'text-green-900' : 'text-red-900'}`}>
                      {testResults.summary}
                    </h4>
                    
                    {/* Validation Checks */}
                    <div className="mt-3 space-y-2">
                      {testResults.checks?.map((check, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {check.status === 'ok' ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span className="font-medium">{check.name}:</span>
                          <span className={check.status === 'ok' ? 'text-green-800' : 'text-red-800'}>
                            {check.message}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Instructions */}
                    {testResults.instructions && testResults.instructions.length > 0 && (
                      <div className="mt-4 p-3 bg-white rounded border border-red-300">
                        <h5 className="font-semibold text-red-900 mb-2">Action Required:</h5>
                        <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                          {testResults.instructions.map((instruction, idx) => (
                            <li key={idx}>{instruction}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Webhook Instructions */}
                    {testResults.webhook_instructions && (
                      <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-300">
                        <h5 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                          <Link2 className="h-4 w-4" />
                          {testResults.webhook_instructions.title}
                        </h5>
                        <div className="space-y-1 text-sm text-blue-800">
                          {testResults.webhook_instructions.steps.map((step, idx) => (
                            <p key={idx}>{step}</p>
                          ))}
                        </div>
                        {testResults.note && (
                          <div className="mt-3 p-2 bg-amber-50 border border-amber-300 rounded">
                            <p className="text-xs font-semibold text-amber-900">{testResults.note}</p>
                          </div>
                        )}
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-blue-900 mb-1">DEPLOYED Webhook URL (Use this in Telnyx):</p>
                          <div className="p-2 bg-white rounded font-mono text-xs break-all border border-blue-300">
                            {testResults.webhook_url}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Export/Import Row */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleExport}
              className="flex-1"
              disabled={!existingSettings?.phone_number}
            >
              <Download className="mr-2 h-4 w-4" />
              Export Settings
            </Button>
            <Button 
              variant="outline" 
              onClick={handleImport}
              className="flex-1"
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Settings
            </Button>
          </div>
          
          {/* Save/Delete Row */}
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={handleDelete}
              disabled={!existingSettings?.api_key_set}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Delete Settings
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-telnyx-settings">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Telnyx Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// LinkedIn Local Agent Card
const LinkedInAgentCard = () => {
  const msg = useAppMessage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await api.get(`${API}/linkedin-msg/agent/status`);
      setStatus(res.data);
    } catch {
      setStatus({ online: false, has_token: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const setupAndDownload = async () => {
    setDownloading(true);
    try {
      // Create token if needed, then download ZIP (token is embedded server-side)
      if (!status?.has_token) {
        await api.post(`${API}/linkedin-msg/agent/token`);
      }
      const res = await api.get(`${API}/linkedin-msg/agent/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'jtcrm-linkedin-agent.zip';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded! Extract the ZIP and run install.bat (Windows) or install.sh (Mac)');
      fetchStatus();
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const revoke = async () => {
    if (!(await msg.confirm('Revoke agent access?', 'Revoke agent access? The running agent will stop within 60 seconds.', { confirmLabel: 'Revoke', destructive: true }))) return;
    setRevoking(true);
    try {
      await api.delete(`${API}/linkedin-msg/agent/token`);
      toast.success('Agent access revoked');
      fetchStatus();
    } catch {
      toast.error('Revoke failed');
    } finally {
      setRevoking(false);
    }
  };

  const online = status?.online;
  const hasToken = status?.has_token;
  const lastSeen = status?.last_seen ? new Date(status.last_seen) : null;
  const tokenCreated = status?.token_created_at ? new Date(status.token_created_at) : null;

  if (loading) return (
    <Card className="border-[#0A66C2]/20">
      <CardContent className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </CardContent>
    </Card>
  );

  return (
    <Card className="border-[#0A66C2]/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <LinkedInIcon />
          <span>LinkedIn Local Agent</span>
          <Badge className={online
            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 ml-auto'
            : hasToken
              ? 'bg-amber-100 text-amber-800 border border-amber-200 ml-auto'
              : 'bg-gray-100 text-gray-600 border border-gray-200 ml-auto'
          }>
            <span className={`mr-1.5 inline-block w-1.5 h-1.5 rounded-full ${
              online ? 'bg-emerald-500 animate-pulse' : hasToken ? 'bg-amber-400' : 'bg-gray-400'
            }`} />
            {online ? 'Online' : hasToken ? 'Offline' : 'Not set up'}
          </Badge>
        </CardTitle>
        <CardDescription>
          Campaigns run from your own PC — same IP as your LinkedIn, no detection risk. No VPN needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Status row */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-3">
          <Monitor className={`h-9 w-9 p-2 rounded-lg shrink-0 ${online ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800">
              {online ? `Running on ${status.hostname}` : hasToken ? 'Agent installed — not running' : 'Agent not installed'}
            </p>
            <p className="text-xs text-slate-500">
              {lastSeen
                ? `Last seen ${format(lastSeen, 'MMM d, h:mm a')}`
                : tokenCreated
                  ? `Token created ${format(tokenCreated, 'MMM d')} · start start_agent.bat to go online`
                  : 'Download and run the agent on your PC'}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Button
            onClick={setupAndDownload}
            disabled={downloading}
            size="sm"
            className="bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}
            {hasToken ? 'Re-download Agent' : 'Download Agent'}
          </Button>
          {hasToken && (
            <Button
              variant="outline"
              size="sm"
              onClick={revoke}
              disabled={revoking}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              {revoking ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <XCircle className="h-4 w-4 mr-1.5" />}
              Revoke Access
            </Button>
          )}
        </div>

        {/* Instructions — only show when not set up */}
        {!hasToken && (
          <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2.5 space-y-1.5 text-xs text-slate-600">
            <p className="font-medium text-slate-700">Quick setup:</p>
            <ol className="list-decimal list-inside space-y-1 ml-0.5">
              <li>Install Python 3.10+ from <span className="text-blue-600">python.org</span> if not already</li>
              <li>Click <strong>Download Agent</strong> above and extract the ZIP</li>
              <li>Run <code className="bg-white border border-slate-200 px-1 rounded">install.bat</code> once to install dependencies</li>
              <li>Run <code className="bg-white border border-slate-200 px-1 rounded">start_agent.bat</code> — keep the window open during campaigns</li>
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Social Accounts Manager Component
const SocialAccountsManager = () => {
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);

  useEffect(() => {
    fetchSocialAccounts();
    
    // Check URL for success/error from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    
    if (success === 'linkedin_connected') {
      toast.success('LinkedIn account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname + '?tab=social');
    } else if (success === 'x_connected') {
      toast.success('X account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname + '?tab=social');
    } else if (error) {
      toast.error(`Connection failed: ${error.replace(/_/g, ' ')}`);
      window.history.replaceState({}, '', window.location.pathname + '?tab=social');
    }
  }, []);

  const fetchSocialAccounts = async () => {
    try {
      const res = await api.get(`${API}/social/accounts`);
      setSocialAccounts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch social accounts');
    } finally {
      setLoading(false);
    }
  };

  const connectLinkedIn = async () => {
    setConnecting('linkedin');
    try {
      const res = await api.get(`${API}/social/oauth/linkedin`);
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error('Failed to start LinkedIn connection');
      setConnecting(null);
    }
  };

  const connectX = async () => {
    setConnecting('x');
    try {
      const res = await api.get(`${API}/social/oauth/x`);
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error('Failed to start X connection');
      setConnecting(null);
    }
  };

  const disconnectAccount = async (provider) => {
    try {
      await api.delete(`${API}/social/accounts/${provider}`);
      toast.success(`${provider} account disconnected`);
      fetchSocialAccounts();
    } catch (error) {
      toast.error('Failed to disconnect account');
    }
  };

  const testConnection = async (provider) => {
    setTestingConnection(provider);
    try {
      if (provider === 'linkedin') {
        // Test LinkedIn by fetching profile
        const res = await api.get(`${API}/social/linkedin/profile`);
        if (res.data.success) {
          toast.success(`LinkedIn connection working! Connected as ${res.data.name || 'user'}`);
        } else {
          toast.error('LinkedIn connection test failed');
        }
      } else if (provider === 'x') {
        // Test X by fetching profile (uses owner tokens)
        const res = await api.get(`${API}/social/x/profile`);
        if (res.data.success) {
          toast.success(`X connection working! Connected as @${res.data.username || res.data.name || 'user'}`);
        } else {
          toast.error('X connection test failed');
        }
      }
    } catch (error) {
      toast.error(`Connection test failed: ${error.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const linkedInAccount = socialAccounts.find(a => a.provider === 'linkedin');
  const xAccount = socialAccounts.find(a => a.provider === 'x');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* LinkedIn Connection */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#0077B5] flex items-center justify-center">
              <LinkedInIcon />
            </div>
            <div>
              <p className="font-medium">LinkedIn</p>
              {linkedInAccount ? (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Connected as {linkedInAccount.account_name}
                </p>
              ) : (
                <p className="text-sm text-slate-500">Post professional content to your profile</p>
              )}
            </div>
          </div>
          {linkedInAccount ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => testConnection('linkedin')}
                disabled={testingConnection === 'linkedin'}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                title="Test Connection"
              >
                {testingConnection === 'linkedin' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => disconnectAccount('linkedin')}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Unlink className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
            </div>
          ) : (
            <Button
              onClick={connectLinkedIn}
              disabled={connecting === 'linkedin'}
              className="bg-[#0077B5] hover:bg-[#006097]"
            >
              {connecting === 'linkedin' ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Link2 className="h-4 w-4 mr-1" />
              )}
              Connect
            </Button>
          )}
        </div>
      </div>

      {/* X/Twitter Connection */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <XIcon />
            </div>
            <div>
              <p className="font-medium">X (Twitter)</p>
              {xAccount ? (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Connected as {xAccount.account_name}
                </p>
              ) : (
                <p className="text-sm text-slate-500">Share updates with your audience</p>
              )}
            </div>
          </div>
          {xAccount ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => testConnection('x')}
                disabled={testingConnection === 'x'}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                title="Test Connection"
              >
                {testingConnection === 'x' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => disconnectAccount('x')}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Unlink className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
            </div>
          ) : (
            <Button
              onClick={connectX}
              disabled={connecting === 'x'}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {connecting === 'x' ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Link2 className="h-4 w-4 mr-1" />
              )}
              Connect
            </Button>
          )}
        </div>
        <p className="text-xs text-amber-600 mt-3 bg-amber-50 p-2 rounded">
          <strong>Note:</strong> X/Twitter requires your app to be configured as a &quot;Web App&quot; with OAuth 1.0a User Context permissions in the 
          <a href="https://developer.twitter.com/en/portal/projects" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline ml-1">
            Twitter Developer Portal
          </a>. 
          Set the callback URL to: <code className="bg-white px-1 rounded text-xs">{window.location.origin}/api/social/oauth/callback/x</code>
        </p>
      </div>

      <div className="bg-slate-50 rounded-lg p-4">
        <p className="text-sm text-slate-600">
          <strong>How it works:</strong> Once connected, you can generate AI-powered social posts from the
          <a href="/admin/social" className="text-indigo-600 hover:underline mx-1">Social page</a>
          and post them directly to your connected accounts with one click.
        </p>
      </div>
    </div>
  );
};

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determine initial tab based on URL params
  const getInitialTab = () => {
    if (searchParams.get('email_success') || searchParams.get('email_error')) {
      return 'email';
    }
    return 'personal';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  
  // Profile state
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [jobdivaId, setJobdivaId] = useState(user?.jobdiva_id || "");
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Email accounts state
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [integrationStatus, setIntegrationStatus] = useState({ gmail: {}, office365: {} });
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [disconnectDialog, setDisconnectDialog] = useState({ open: false, account: null });
  const [disconnecting, setDisconnecting] = useState(false);

  // Handle OAuth callback messages from URL params
  useEffect(() => {
    const emailSuccess = searchParams.get('email_success');
    const emailError = searchParams.get('email_error');
    
    if (emailSuccess) {
      toast.success(emailSuccess);
      searchParams.delete('email_success');
      setSearchParams(searchParams, { replace: true });
      fetchEmailAccounts();
      setActiveTab('email');
    }
    
    if (emailError) {
      toast.error(emailError);
      searchParams.delete('email_error');
      setSearchParams(searchParams, { replace: true });
      setActiveTab('email');
    }
  }, [searchParams, setSearchParams]);

  // Fetch email accounts and integration status on mount
  useEffect(() => {
    fetchEmailAccounts();
    fetchIntegrationStatus();
  }, []);

  const fetchEmailAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await api.get(`${API}/email-accounts`);
      setEmailAccounts(response.data || []);
    } catch (error) {
      console.error("Failed to fetch email accounts:", error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchIntegrationStatus = async () => {
    try {
      const response = await api.get(`${API}/email-accounts/status`);
      setIntegrationStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch integration status:", error);
    }
  };

  const handleConnectEmail = async (provider) => {
    setConnectingProvider(provider);
    try {
      const response = await api.get(`${API}/email-accounts/oauth/url?provider=${provider}`);
      window.location.href = response.data.oauth_url;
    } catch (error) {
      const message = error.response?.data?.detail || `Failed to initiate ${provider} connection`;
      toast.error(message);
      setConnectingProvider(null);
    }
  };

  const handleDisconnectEmail = async () => {
    if (!disconnectDialog.account) return;
    
    setDisconnecting(true);
    try {
      await api.delete(`${API}/email-accounts/${disconnectDialog.account.id}`);
      toast.success(`${disconnectDialog.account.email} disconnected`);
      setDisconnectDialog({ open: false, account: null });
      fetchEmailAccounts();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to disconnect email account";
      toast.error(message);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    if (!fullName.trim()) {
      toast.error("Name is required");
      return;
    }

    setSavingProfile(true);
    try {
      await api.put(`${API}/auth/profile`, {
        full_name: fullName,
        phone: phone || null,
        jobdiva_id: jobdivaId || null,
      });
      toast.success("Profile updated successfully");
      await checkAuth();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update profile";
      toast.error(message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error("Current password is required");
      return;
    }
    
    if (!newPassword) {
      toast.error("New password is required");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSavingPassword(true);
    try {
      await api.put(`${API}/auth/profile`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setSavingPassword(false);
    }
  };

  // Get accounts by provider
  const getAccountsByProvider = (provider) => {
    return emailAccounts.filter(acc => acc.provider === provider && acc.is_active);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Profile Settings
        </h1>
        <p className="text-slate-600 mt-1">
          Manage your account information
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-7 mb-6 h-10">
          <TabsTrigger value="personal" className="flex items-center gap-1.5 text-xs" data-testid="tab-personal">
            <User className="h-3.5 w-3.5 shrink-0" />
            Personal
          </TabsTrigger>
          <TabsTrigger value="password" className="flex items-center gap-1.5 text-xs" data-testid="tab-password">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            Password
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-1.5 text-xs" data-testid="tab-notifications">
            <Bell className="h-3.5 w-3.5 shrink-0" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="telnyx" className="flex items-center gap-1.5 text-xs" data-testid="tab-telnyx">
            <PhoneCall className="h-3.5 w-3.5 shrink-0" />
            Telnyx
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-1.5 text-xs" data-testid="tab-email">
            <Mail className="h-3.5 w-3.5 shrink-0" />
            Email
          </TabsTrigger>
          <TabsTrigger value="audio" className="flex items-center gap-1.5 text-xs" data-testid="tab-audio">
            <Volume2 className="h-3.5 w-3.5 shrink-0" />
            Audio
          </TabsTrigger>
          <TabsTrigger value="social" className="flex items-center gap-1.5 text-xs" data-testid="tab-social">
            <Share2 className="h-3.5 w-3.5 shrink-0" />
            Social
          </TabsTrigger>
        </TabsList>

        {/* Personal Tab */}
        <TabsContent value="personal">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Personal Information</CardTitle>
                  <CardDescription>Update your name and contact details</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-5">
                {/* Read-only account field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-1.5">
                    Email
                    <Lock className="h-3.5 w-3.5 text-slate-400" />
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="bg-slate-50 text-slate-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-slate-400">
                    Your email address is managed by your account and cannot be changed.
                  </p>
                </div>

                <Separator />

                {/* Editable fields */}
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={savingProfile}
                      data-testid="profile-fullname-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={savingProfile}
                      data-testid="profile-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="jobdivaId">JobDiva ID</Label>
                    <Input
                      id="jobdivaId"
                      type="text"
                      placeholder="e.g., 1358065"
                      value={jobdivaId}
                      onChange={(e) => setJobdivaId(e.target.value)}
                      disabled={savingProfile}
                    />
                  </div>
                </div>

                <Separator />

                <div className="flex justify-end">
                  <Button type="submit" disabled={savingProfile} className="min-w-32" data-testid="save-profile-button">
                    {savingProfile ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Lock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Change Password</CardTitle>
                  <CardDescription>Update your password to keep your account secure</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={savingPassword}
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={savingPassword}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Minimum 8 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={savingPassword}
                    data-testid="confirm-new-password-input"
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={savingPassword} data-testid="change-password-button">
                    {savingPassword ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <TaskNotificationManager />
          <EmailCCManager />
          <NotificationSettingsManager />
          <NotificationSoundsManager />
        </TabsContent>

        {/* Telnyx Tab */}
        <TabsContent value="telnyx" className="space-y-6">
          <TelnyxSettingsManager />
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Connected Email Accounts</CardTitle>
                  <CardDescription>Connect your email accounts to send messages from the CRM</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingAccounts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  {/* Office365 Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Office365Icon />
                        <span className="font-medium">Office 365</span>
                        {getAccountsByProvider('office365').length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {getAccountsByProvider('office365').length} connected
                          </Badge>
                        )}
                      </div>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleConnectEmail('office365')}
                        disabled={!integrationStatus.office365?.configured || connectingProvider === 'office365'}
                        data-testid="connect-office365-btn"
                      >
                        {connectingProvider === 'office365' ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4 mr-1" />
                        )}
                        Add Account
                      </Button>
                    </div>
                    
                    {!integrationStatus.office365?.configured && (
                      <p className="text-sm text-slate-500 pl-7">Office365 integration not configured</p>
                    )}
                    
                    {/* List of connected Office365 accounts */}
                    {getAccountsByProvider('office365').map((account) => (
                      <div key={account.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 ml-7">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-slate-200">
                            <Office365Icon />
                          </div>
                          <div>
                            <div className="font-medium text-sm flex items-center gap-2">
                              {account.email}
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Connected
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500">
                              Connected {account.connected_at
                                ? format(new Date(account.connected_at), 'MMM d, yyyy')
                                : ''}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDisconnectDialog({ open: true, account })}
                          data-testid={`disconnect-${account.id}`}
                        >
                          <Unlink className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}

                    {/* Calendar sync info */}
                    {getAccountsByProvider('office365').length > 0 ? (
                      <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 ml-7">
                        <Calendar className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <span className="font-medium">Calendar sync enabled</span> &mdash; meetings booked by AI calls will auto-create Outlook calendar events with invites.
                        </div>
                      </div>
                    ) : integrationStatus.office365?.configured ? (
                      <p className="text-xs text-slate-500 pl-7">Connect Office 365 to enable email sending and calendar sync for AI-booked meetings.</p>
                    ) : null}
                  </div>

                  <Separator />

                  {/* Gmail Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GmailIcon />
                        <span className="font-medium">Gmail</span>
                        {getAccountsByProvider('gmail').length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {getAccountsByProvider('gmail').length} connected
                          </Badge>
                        )}
                      </div>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleConnectEmail('gmail')}
                        disabled={!integrationStatus.gmail?.configured || connectingProvider === 'gmail'}
                        data-testid="connect-gmail-btn"
                      >
                        {connectingProvider === 'gmail' ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4 mr-1" />
                        )}
                        Add Account
                      </Button>
                    </div>
                    
                    {!integrationStatus.gmail?.configured && (
                      <p className="text-sm text-slate-500 pl-7">Gmail integration not configured</p>
                    )}
                    
                    {/* List of connected Gmail accounts */}
                    {getAccountsByProvider('gmail').map((account) => (
                      <div key={account.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 ml-7">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-slate-200">
                            <GmailIcon />
                          </div>
                          <div>
                            <div className="font-medium text-sm flex items-center gap-2">
                              {account.email}
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Connected
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500">
                              Connected {account.connected_at 
                                ? format(new Date(account.connected_at), 'MMM d, yyyy')
                                : ''}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDisconnectDialog({ open: true, account })}
                          data-testid={`disconnect-${account.id}`}
                        >
                          <Unlink className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Info message */}
                  {(!integrationStatus.gmail?.configured && !integrationStatus.office365?.configured) && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium">Email integrations not configured</p>
                        <p className="text-amber-700 mt-0.5">
                          Contact your administrator to set up Gmail or Office365 OAuth credentials to enable email sending.
                        </p>
                      </div>
                    </div>
                  )}

                  {(emailAccounts.length > 0) && (
                    <p className="text-xs text-slate-500">
                      Connected accounts will be used to send emails from the CRM. You can connect multiple accounts per provider.
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Email Signature */}
          <EmailSignatureEditor />
        </TabsContent>

        {/* Audio Tab */}
        <TabsContent value="audio" className="space-y-6">
          <AudioSettingsManager />
        </TabsContent>

        {/* Social Tab */}
        <TabsContent value="social" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-indigo-600" />
                Social Media Accounts
              </CardTitle>
              <CardDescription>
                Connect your LinkedIn and X (Twitter) accounts to post directly from the Social page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <SocialAccountsManager />
            </CardContent>
          </Card>
          <LinkedInAgentCard />
        </TabsContent>
      </Tabs>

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={disconnectDialog.open} onOpenChange={(open) => !open && setDisconnectDialog({ open: false, account: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Email Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect <strong>{disconnectDialog.account?.email}</strong>? 
              You will no longer be able to send emails from this account through the CRM.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDisconnectEmail} 
              disabled={disconnecting}
              className="bg-red-600 hover:bg-red-700"
            >
              {disconnecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                "Disconnect"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
